import { useEffect, useMemo, useState, useCallback } from 'react';
import { cn } from '@/lib/utils/cn';
import {
  FIRM_POLICY_CATEGORY_ORDER,
  type FirmNotificationPolicy,
} from '@/lib/api/firm-notifications';
import type { GetPreferencesResult, DigestCadence } from '@/lib/api/my-notifications';

/**
 * My-notifications channel overrides editor (T-0112 / C20-122).
 *
 * Mirrors the prototype's `screens-gaps.js → firmMyNotifs()` table: a
 * per-event channel-toggle matrix that *deviates* from the firm-wide
 * defaults rendered by `notification-policy-editor.tsx`. The shape we
 * read from `GET /api/v1/notifications/preferences` is sparse — only the
 * rows the caller has explicitly toggled away from the firm default — so
 * we render the union of (firm rows) + (caller's per-event subscriptions)
 * and surface a "Custom" chip when any cell deviates.
 *
 * Behavioural notes:
 *   - Channel toggles default to the firm row's value unless an explicit
 *     caller subscription says otherwise.
 *   - Per-engagement mutes render as togglable scope-chips below the row;
 *     a strikethrough variant indicates the row is muted on that
 *     engagement.
 *   - Digest cadence is a separate, route-level control — see
 *     `my-notifications.ts` for the backend gap note.
 *   - `onSaveGlobal` and `onSaveEngagement` are the route-supplied
 *     mutations. The component computes the diff between the seeded state
 *     and the draft, batches the global subscription writes into a single
 *     PUT, and emits per-engagement PUTs for each engagement that has
 *     dirty mute changes.
 */

const SEVERITY_DOT_CLASS: Record<string, string> = {
  info: 'bg-posture-healthy',
  warning: 'bg-posture-at-risk',
  high: 'bg-posture-at-risk',
  critical: 'bg-posture-critical',
};

/** What the user sees for each event_type row. */
interface RowState {
  /** Canonical event_type. */
  event_type: string;
  /** Display label from the firm policy. */
  label: string;
  /** Severity (firm-set; user can't override on their per-event view). */
  severity: string;
  /** Category — used for grouping. */
  category: string;
  /** Is the user currently subscribed (post-override)? */
  is_subscribed: boolean;
  /** Firm-default subscribed for diff display. */
  firm_subscribed: boolean;
  /** Per-engagement mutes for THIS event (engagement_id → is_muted). */
  mutes: Map<string, boolean>;
  /** Has the user explicitly overridden the firm default for this row? */
  is_override: boolean;
}

export interface EngagementSummary {
  engagement_id: string;
  client_name: string;
}

export interface ChannelOverridesProps {
  firmPolicies: ReadonlyArray<FirmNotificationPolicy>;
  myPrefs: GetPreferencesResult;
  myEngagements: ReadonlyArray<EngagementSummary>;
  digestCadence: DigestCadence;
  onDigestCadenceChange: (next: DigestCadence) => void;
  onSaveGlobal: (subscriptions: Array<{ event_type: string; is_subscribed: boolean }>) => void;
  onSaveEngagement: (
    engagementId: string,
    mutes: Array<{ event_type: string; is_muted: boolean }>,
  ) => void;
  saving?: boolean;
}

export function ChannelOverrides({
  firmPolicies,
  myPrefs,
  myEngagements,
  digestCadence,
  onDigestCadenceChange,
  onSaveGlobal,
  onSaveEngagement,
  saving,
}: ChannelOverridesProps) {
  const seedDraft = useCallback((): RowState[] => {
    const subByType = new Map(myPrefs.global.map((g) => [g.event_type, g.is_subscribed]));
    const mutesByType = new Map<string, Map<string, boolean>>();
    for (const m of myPrefs.per_engagement_mutes) {
      const map = mutesByType.get(m.event_type) ?? new Map<string, boolean>();
      map.set(m.engagement_id, m.is_muted);
      mutesByType.set(m.event_type, map);
    }
    return firmPolicies.map<RowState>((p) => {
      // The firm-side `in_app + email + digest` triple is treated as the
      // firm default for subscription state — if any of the three is on,
      // the user is subscribed unless they explicitly unsubscribed.
      const firmSubscribed = p.in_app || p.email || p.digest;
      const userSub = subByType.get(p.event_type);
      const isSubscribed = userSub === undefined ? firmSubscribed : userSub;
      return {
        event_type: p.event_type,
        label: p.label,
        severity: p.severity,
        category: p.category,
        is_subscribed: isSubscribed,
        firm_subscribed: firmSubscribed,
        mutes: mutesByType.get(p.event_type) ?? new Map(),
        is_override: userSub !== undefined && userSub !== firmSubscribed,
      };
    });
  }, [firmPolicies, myPrefs]);

  const [draft, setDraft] = useState<RowState[]>(seedDraft);
  useEffect(() => {
    setDraft(seedDraft());
  }, [seedDraft]);

  const updateRow = useCallback((eventType: string, patch: Partial<RowState>) => {
    setDraft((prev) => prev.map((r) => (r.event_type === eventType ? { ...r, ...patch } : r)));
  }, []);

  const toggleSub = useCallback((eventType: string) => {
    setDraft((prev) =>
      prev.map((r) => {
        if (r.event_type !== eventType) return r;
        const next = !r.is_subscribed;
        return {
          ...r,
          is_subscribed: next,
          is_override: next !== r.firm_subscribed,
        };
      }),
    );
  }, []);

  const toggleMute = useCallback((eventType: string, engagementId: string) => {
    setDraft((prev) =>
      prev.map((r) => {
        if (r.event_type !== eventType) return r;
        const map = new Map(r.mutes);
        const wasMuted = map.get(engagementId) ?? false;
        map.set(engagementId, !wasMuted);
        return { ...r, mutes: map };
      }),
    );
  }, []);

  const dirty = useMemo(() => {
    const seed = seedDraft();
    if (seed.length !== draft.length) return true;
    for (let i = 0; i < draft.length; i += 1) {
      const a = draft[i];
      const b = seed[i];
      if (!a || !b) return true;
      if (a.is_subscribed !== b.is_subscribed) return true;
      if (a.mutes.size !== b.mutes.size) return true;
      for (const [eid, val] of a.mutes) {
        if (b.mutes.get(eid) !== val) return true;
      }
    }
    return false;
  }, [draft, seedDraft]);

  const handleReset = useCallback(() => {
    setDraft(seedDraft());
  }, [seedDraft]);

  const handleSave = useCallback(() => {
    const subs = draft.map((r) => ({
      event_type: r.event_type,
      is_subscribed: r.is_subscribed,
    }));
    onSaveGlobal(subs);
    // Per-engagement mute batches: one PUT per engagement that has any
    // dirty mute changes.
    const dirtyEngagements = new Map<string, Array<{ event_type: string; is_muted: boolean }>>();
    for (const r of draft) {
      for (const [eid, isMuted] of r.mutes) {
        const list = dirtyEngagements.get(eid) ?? [];
        list.push({ event_type: r.event_type, is_muted: isMuted });
        dirtyEngagements.set(eid, list);
      }
    }
    for (const [eid, mutes] of dirtyEngagements) {
      onSaveEngagement(eid, mutes);
    }
  }, [draft, onSaveGlobal, onSaveEngagement]);

  const customCount = draft.filter((r) => r.is_override).length;
  const muteCount = draft.reduce(
    (acc, r) => acc + Array.from(r.mutes.values()).filter(Boolean).length,
    0,
  );

  const grouped = useMemo(() => {
    const byCat = new Map<string, RowState[]>();
    for (const row of draft) {
      const list = byCat.get(row.category) ?? [];
      list.push(row);
      byCat.set(row.category, list);
    }
    return FIRM_POLICY_CATEGORY_ORDER.flatMap((cat) => {
      const list = byCat.get(cat);
      if (!list || list.length === 0) return [];
      return [{ category: cat, rows: list }];
    });
  }, [draft]);

  return (
    <div className="space-y-4">
      <div className="rounded-card border border-ai-surface-border bg-ai-surface p-3 text-body-sm text-ai-ink">
        <strong className="font-semibold">Personal overrides.</strong> Toggles here override the
        firm-wide default for your account only. <em>Custom</em> chips mark events where your
        preference differs from the firm policy. The <em>Active on</em> column lets you mute a row
        on specific engagements without losing it elsewhere. Critical-severity events cannot be
        fully muted by policy.
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <SummaryStat
          label="Channel overrides"
          value={customCount}
          hint={`of ${draft.length} event types`}
        />
        <SummaryStat
          label="Per-engagement mutes"
          value={muteCount}
          hint={`across ${myEngagements.length} engagement${myEngagements.length === 1 ? '' : 's'}`}
        />
        <DigestCadenceCard cadence={digestCadence} onChange={onDigestCadenceChange} />
      </div>

      <div className="overflow-x-auto rounded-card border border-ink-100">
        <table className="w-full border-collapse text-body-sm">
          <thead>
            <tr className="border-b border-ink-100 bg-paper-50 text-left">
              <th
                scope="col"
                className="w-[42%] px-3 py-2 text-meta font-medium uppercase tracking-wide text-ink-500"
              >
                Event
              </th>
              <th
                scope="col"
                className="px-3 py-2 text-center text-meta font-medium uppercase tracking-wide text-ink-500"
              >
                Subscribed
              </th>
              <th
                scope="col"
                className="px-3 py-2 text-meta font-medium uppercase tracking-wide text-ink-500"
              >
                Active on
              </th>
            </tr>
          </thead>
          <tbody>
            {grouped.map(({ category, rows }) => (
              <RowsForCategory
                key={category}
                category={category}
                rows={rows}
                myEngagements={myEngagements}
                onToggleSub={toggleSub}
                onToggleMute={toggleMute}
                updateRow={updateRow}
              />
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-meta text-ink-500">
          {dirty ? 'Unsaved overrides.' : 'No unsaved changes.'}
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleReset}
            disabled={!dirty || saving}
            className={cn(
              'inline-flex h-9 items-center gap-2 rounded-card border border-ink-100 bg-paper-0 px-3 text-body-sm font-medium text-ink-700',
              'hover:bg-paper-50 disabled:cursor-not-allowed disabled:opacity-50',
            )}
          >
            Reset to firm defaults
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!dirty || saving}
            className={cn(
              'inline-flex h-9 items-center gap-2 rounded-card bg-accent-amber-600 px-3 text-body-sm font-medium text-paper-0',
              'hover:bg-accent-amber-700 disabled:cursor-not-allowed disabled:opacity-50',
            )}
          >
            {saving ? 'Saving…' : 'Save preferences'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface RowsForCategoryProps {
  category: string;
  rows: RowState[];
  myEngagements: ReadonlyArray<EngagementSummary>;
  onToggleSub: (et: string) => void;
  onToggleMute: (et: string, eid: string) => void;
  updateRow: (et: string, patch: Partial<RowState>) => void;
}

function RowsForCategory({
  category,
  rows,
  myEngagements,
  onToggleSub,
  onToggleMute,
}: RowsForCategoryProps) {
  return (
    <>
      <tr className="bg-paper-100">
        <td
          colSpan={3}
          className="px-3 py-1.5 text-meta font-semibold uppercase tracking-wider text-ink-700"
        >
          {category} · {rows.length} event{rows.length === 1 ? '' : 's'}
        </td>
      </tr>
      {rows.map((row) => (
        <tr key={row.event_type} className="border-t border-ink-100 align-top">
          <td className="px-3 py-2">
            <div className="flex items-center gap-2">
              <span
                aria-hidden="true"
                className={cn(
                  'inline-block h-2 w-2 rounded-full',
                  SEVERITY_DOT_CLASS[row.severity] ?? 'bg-ink-300',
                )}
              />
              <span className="font-medium text-ink-900">{row.label}</span>
              {row.is_override ? (
                <span className="inline-flex h-5 items-center rounded-chip border border-accent-amber-100 bg-accent-amber-50 px-1.5 text-meta font-medium text-accent-amber-700">
                  Custom
                </span>
              ) : null}
            </div>
            <div className="mt-0.5 font-mono text-meta text-ink-500">{row.event_type}</div>
          </td>
          <td className="px-3 py-2 text-center align-middle">
            <SubToggle
              checked={row.is_subscribed}
              onChange={() => onToggleSub(row.event_type)}
              label={`Subscribed to ${row.label}`}
            />
          </td>
          <td className="px-3 py-2">
            <div className="flex flex-wrap gap-1.5">
              {myEngagements.length === 0 ? (
                <span className="text-meta text-ink-500">No engagements</span>
              ) : (
                myEngagements.map((eng) => {
                  const isMuted = row.mutes.get(eng.engagement_id) ?? false;
                  return (
                    <button
                      key={eng.engagement_id}
                      type="button"
                      onClick={() => onToggleMute(row.event_type, eng.engagement_id)}
                      aria-pressed={isMuted}
                      className={cn(
                        'inline-flex h-6 items-center rounded-chip border px-2 text-meta font-medium transition-colors',
                        isMuted
                          ? 'border-posture-critical bg-posture-critical-tint text-posture-critical line-through'
                          : 'border-ink-100 bg-paper-0 text-ink-700 hover:bg-paper-50',
                      )}
                      title={isMuted ? 'Muted on this engagement' : 'Active on this engagement'}
                    >
                      {eng.client_name}
                    </button>
                  );
                })
              )}
            </div>
          </td>
        </tr>
      ))}
    </>
  );
}

interface SubToggleProps {
  checked: boolean;
  onChange: () => void;
  label: string;
}

function SubToggle({ checked, onChange, label }: SubToggleProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={onChange}
      className={cn(
        'relative inline-flex h-4 w-7 flex-none items-center rounded-full transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600 focus-visible:ring-offset-1',
        checked ? 'bg-posture-healthy' : 'bg-ink-200',
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'absolute inline-block h-3 w-3 rounded-full bg-paper-0 shadow-sm transition-transform',
          checked ? 'translate-x-3.5' : 'translate-x-0.5',
        )}
      />
    </button>
  );
}

function SummaryStat({
  label,
  value,
  hint,
}: {
  label: string;
  value: number | string;
  hint?: string;
}) {
  return (
    <div className="rounded-card border border-ink-100 bg-paper-0 p-3">
      <div className="text-meta uppercase tracking-wider text-ink-500">{label}</div>
      <div className="mt-1 text-h2 font-semibold text-ink-900">{value}</div>
      {hint ? <div className="text-meta text-ink-500">{hint}</div> : null}
    </div>
  );
}

interface DigestCadenceCardProps {
  cadence: DigestCadence;
  onChange: (next: DigestCadence) => void;
}

function DigestCadenceCard({ cadence, onChange }: DigestCadenceCardProps) {
  const OPTIONS: { value: DigestCadence; label: string }[] = [
    { value: 'off', label: 'Off' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
  ];
  return (
    <div className="rounded-card border border-ink-100 bg-paper-0 p-3">
      <div className="text-meta uppercase tracking-wider text-ink-500">Email digest cadence</div>
      <p className="mt-1 text-meta text-accent-amber-700">
        Coming soon — this preference is not yet saved to the server.
      </p>
      <div className="mt-2 flex gap-1.5">
        {OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            aria-pressed={cadence === opt.value}
            className={cn(
              'inline-flex h-7 flex-1 items-center justify-center rounded-card border px-2 text-meta font-medium transition-colors',
              cadence === opt.value
                ? 'border-accent-amber-600 bg-accent-amber-50 text-accent-amber-700'
                : 'border-ink-100 bg-paper-0 text-ink-700 hover:bg-paper-50',
            )}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}
